import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Section } from '../ui/Section';
import { Check, Brain, Moon, Zap, DollarSign, Shield, XCircle } from 'lucide-react';
export function DecisionSection() {
  return <Section background="white" data-id="element-35">
      <div className="max-w-4xl mx-auto" data-id="element-36">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-37">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-brand-charcoal-dark mb-8" data-id="element-38">
            ES EL MOMENTO DE DECIDIR
          </h2>

          <div className="text-lg text-brand-charcoal/80 leading-relaxed space-y-4 max-w-3xl mx-auto text-left md:text-center" data-id="element-39">
            <p data-id="element-40">Llegaste hasta aquí por algo.</p>
            <p data-id="element-41">
              Porque reconociste esa sensación. La presión en el pecho. Las
              pestañas abiertas. El cerebro que no arranca. Los días que
              terminan sin haber ejecutado lo que realmente importaba.
            </p>
            <p className="font-bold text-brand-charcoal-dark" data-id="element-42">
              Y sabes que no es sostenible.
            </p>
            <p data-id="element-43">
              No puedes seguir operando en modo supervivencia y pretender que
              estás escalando un negocio. No puedes tomar decisiones de alto
              nivel con un sistema nervioso en alarma constante. No puedes
              liderar un equipo cuando por dentro estás agotado.
            </p>
            <p data-id="element-44">Tu negocio no necesita más estrategia.</p>
            <p className="text-xl font-serif italic text-brand-gold-dark" data-id="element-45">
              Te necesita a ti. Lúcido. Enfocado. En control.
            </p>
            <p className="font-bold" data-id="element-46">Y eso empieza hoy. En 7 minutos.</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-47">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-cream p-8 rounded-xl border border-brand-gold/20" data-id="element-48">
            <h3 className="text-xl font-bold text-brand-charcoal-dark mb-6 flex items-center gap-2" data-id="element-49">
              <Check className="h-6 w-6 text-brand-sage" data-id="element-50" />
              ESTO ES LO QUE PASA SI HACES CLIC AHORA
            </h3>
            <ul className="space-y-4" data-id="element-51">
              {['Obtienes acceso inmediato al Manual Operativo Cortisol Off', 'Recibes los 3 bonos de ejecución valorados en $141 — completamente gratis', 'Tienes acceso de por vida — sin suscripciones, sin pagos recurrentes', 'Estás protegido por la Garantía de Hierro de 30 días', 'En menos de 7 minutos puedes ejecutar tu primer reseteo'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-52">
                  <Check className="h-5 w-5 text-brand-sage flex-shrink-0 mt-0.5" data-id="element-53" />
                  <span className="text-brand-charcoal/80" data-id="element-54">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-brand-charcoal-dark p-8 rounded-xl text-white" data-id="element-55">
            <h3 className="text-xl font-bold text-brand-cream mb-6 flex items-center gap-2" data-id="element-56">
              <XCircle className="h-6 w-6 text-red-400" data-id="element-57" />
              ESTO ES LO QUE PASA SI NO LO HACES
            </h3>
            <div className="space-y-4 text-brand-cream/70" data-id="element-58">
              <p data-id="element-59">
                Mañana son las 3 de la tarde. Tienes 40 pestañas abiertas,
                mensajes acumulados y esa presión en el pecho que ya conoces de
                memoria.
              </p>
              <p data-id="element-60">Tu cerebro no arranca. Intentas con otro café. No funciona.</p>
              <p data-id="element-61">
                Otro día operando por debajo de tu potencial real. Otra noche
                dando vueltas en la cama con la mente encendida.
              </p>
              <p className="text-brand-gold font-medium pt-4 border-t border-white/10" data-id="element-62">
                El costo de no actuar hoy no es $6.36.
                <br data-id="element-63" />
                El costo real es otro mes, otro trimestre, otro año operando en
                modo supervivencia.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-64">
          <h3 className="text-2xl font-serif font-bold text-center text-brand-charcoal-dark mb-8" data-id="element-65">
            TU DECISIÓN
          </h3>

          <div className="bg-white rounded-xl shadow-lg border border-brand-gold/20 overflow-hidden" data-id="element-66">
            <div className="grid grid-cols-2 bg-brand-cream-dark border-b border-brand-gold/20" data-id="element-67">
              <div className="p-4 text-center font-bold text-brand-charcoal-dark" data-id="element-68">
                SI ACTÚAS HOY
              </div>
              <div className="p-4 text-center font-bold text-brand-charcoal/50 border-l border-brand-gold/20" data-id="element-69">
                SI NO ACTÚAS
              </div>
            </div>

            <div className="divide-y divide-brand-gold/10" data-id="element-70">
              {[{
              icon: Brain,
              pro: 'Claridad mental en 7 minutos',
              con: 'Neblina mental constante'
            }, {
              icon: Moon,
              pro: 'Sueño profundo y reparador',
              con: 'Noches dando vueltas'
            }, {
              icon: Zap,
              pro: 'Enfoque de las 9AM a las 5PM',
              con: 'Colapso de las 3PM garantizado'
            }, {
              icon: DollarSign,
              pro: '$6.36 inversión única',
              con: '$0 hoy, costo real mañana'
            }, {
              icon: Shield,
              pro: 'Garantía de 30 días',
              con: 'Sin red de seguridad'
            }].map((row, i) => <div key={i} className="grid grid-cols-2" data-id="element-71">
                  <div className="p-4 flex items-center gap-3 text-brand-charcoal-dark font-medium" data-id="element-72">
                    <row.icon className="h-5 w-5 text-brand-gold-dark hidden sm:block" data-id="element-73" />
                    <span className="text-sm sm:text-base" data-id="element-74">{row.pro}</span>
                  </div>
                  <div className="p-4 flex items-center text-brand-charcoal/50 border-l border-brand-gold/10" data-id="element-75">
                    <span className="text-sm sm:text-base" data-id="element-76">{row.con}</span>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}